import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Key, TrendingUp, AlertTriangle, Lightbulb } from "lucide-react";
import type { KeywordAnalysis, KeywordItem } from "@/types/analysis";

interface KeywordsTabProps {
  data?: KeywordAnalysis;
}

const statusConfig: Record<KeywordItem["status"], { label: string; className: string }> = {
  optimal: { label: "Optimal", className: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
  "over-optimized": { label: "Over-optimized", className: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  "under-used": { label: "Under-used", className: "bg-blue-500/15 text-blue-400 border-blue-500/30" },
};

const intentConfig: Record<string, { label: string; className: string }> = {
  informational: { label: "Informational", className: "bg-blue-500/15 text-blue-400 border-blue-500/30" },
  navigational: { label: "Navigational", className: "bg-purple-500/15 text-purple-400 border-purple-500/30" },
  commercial: { label: "Commercial", className: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  transactional: { label: "Transactional", className: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
};

const locationLabels: Record<string, string> = {
  title: "Title",
  h1: "H1",
  h2: "H2",
  "meta-description": "Meta",
  body: "Body",
};

export const KeywordsTab = ({ data }: KeywordsTabProps) => {
  if (!data) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="p-8 text-center text-muted-foreground">
          <Key className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p>Keyword analysis data is not available for this report.</p>
          <p className="text-sm mt-1">Try running a new analysis to generate keyword insights.</p>
        </CardContent>
      </Card>
    );
  }

  const chartData = data.keywords
    .slice(0, 10)
    .map((kw) => ({
      name: kw.text.length > 20 ? kw.text.slice(0, 18) + "…" : kw.text,
      density: kw.density,
      status: kw.status,
    }));

  const barColor = (status: string) => {
    if (status === "optimal") return "hsl(152, 70%, 45%)";
    if (status === "over-optimized") return "hsl(38, 95%, 55%)";
    return "hsl(210, 90%, 55%)";
  };

  const optimalCount = data.keywords.filter((k) => k.status === "optimal").length;
  const overCount = data.keywords.filter((k) => k.status === "over-optimized").length;
  const underCount = data.keywords.filter((k) => k.status === "under-used").length;

  return (
    <div className="space-y-6">
      {/* Score + stats row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="p-5 text-center">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Keyword Score</p>
            <p className="text-4xl font-bold text-primary">{data.overallScore}</p>
            <Progress value={data.overallScore} className="mt-2 h-1.5" />
            <p className="text-xs text-muted-foreground mt-1">{data.confidence}% confidence</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-5 text-center">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Primary Keyword</p>
            <p className="text-lg font-semibold text-foreground">{data.primaryKeyword}</p>
            <p className="text-xs text-muted-foreground mt-1">{data.totalWords.toLocaleString()} total words</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-5 text-center">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Keywords Found</p>
            <p className="text-3xl font-bold text-foreground">{data.keywords.length}</p>
            <div className="flex justify-center gap-2 mt-1.5">
              <span className="text-xs text-emerald-400">{optimalCount} optimal</span>
              <span className="text-xs text-amber-400">{overCount} over</span>
              <span className="text-xs text-blue-400">{underCount} under</span>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-5 text-center">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Avg Density</p>
            <p className="text-3xl font-bold text-foreground">
              {data.keywords.length > 0
                ? (data.keywords.reduce((s, k) => s + k.density, 0) / data.keywords.length).toFixed(2)
                : "0"}%
            </p>
            <p className="text-xs text-muted-foreground mt-1">across all keywords</p>
          </CardContent>
        </Card>
      </div>

      {/* Bar chart */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-primary" /> Top Keyword Densities
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} layout="vertical" margin={{ left: 10, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(228,12%,18%)" />
                <XAxis type="number" tickFormatter={(v) => `${v}%`} stroke="hsl(215,15%,55%)" fontSize={12} />
                <YAxis type="category" dataKey="name" width={140} stroke="hsl(215,15%,55%)" fontSize={12} />
                <Tooltip
                  contentStyle={{ background: "hsl(228,18%,10%)", border: "1px solid hsl(228,12%,18%)", borderRadius: "8px", fontSize: "12px" }}
                  formatter={(value: number) => [`${value}%`, "Density"]}
                />
                <Bar dataKey="density" radius={[0, 4, 4, 0]}>
                  {chartData.map((entry, idx) => (
                    <Cell key={idx} fill={barColor(entry.status)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Keywords table */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Key className="w-4 h-4 text-primary" /> All Keywords
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-muted-foreground">
                  <th className="text-left py-2 pr-4 font-medium">Keyword</th>
                  <th className="text-center py-2 px-3 font-medium">Freq</th>
                  <th className="text-center py-2 px-3 font-medium">Density</th>
                  <th className="text-left py-2 px-3 font-medium">Locations</th>
                  <th className="text-center py-2 px-3 font-medium">Intent</th>
                  <th className="text-center py-2 pl-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.keywords.map((kw, i) => {
                  const sc = statusConfig[kw.status];
                  return (
                    <tr key={i} className="border-b border-border/50 hover:bg-secondary/30 transition-colors">
                      <td className="py-2.5 pr-4 font-medium text-foreground">{kw.text}</td>
                      <td className="py-2.5 px-3 text-center text-muted-foreground">{kw.frequency}</td>
                      <td className="py-2.5 px-3 text-center font-mono text-foreground">{kw.density}%</td>
                      <td className="py-2.5 px-3">
                        <div className="flex flex-wrap gap-1">
                          {kw.locations.map((loc) => (
                            <span key={loc} className="text-[10px] px-1.5 py-0.5 rounded bg-secondary text-secondary-foreground">
                              {locationLabels[loc] || loc}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        {kw.intent && intentConfig[kw.intent] ? (
                          <Badge variant="outline" className={intentConfig[kw.intent].className}>{intentConfig[kw.intent].label}</Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="py-2.5 pl-3 text-center">
                        <Badge variant="outline" className={sc.className}>{sc.label}</Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Recommendations */}
      {data.recommendations.length > 0 && (
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Lightbulb className="w-4 h-4 text-amber-400" /> Recommendations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {data.recommendations.map((rec, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 text-amber-400 shrink-0" />
                  {rec}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
